<template>
  <div v-if="course" class="w-full h-full">
    <!-- header -->
    <div class="flex item-center justify-between p-[12px] border-b border-gray-200 sticky top-0 left-0 w-full z-[1000]">
      <div class="flex items-center gap-3">
        <button @click="closeModal" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        <div class="text-xl font-medium">{{ course.title }}</div>
      </div>
      <div v-if="isAdmin" class="flex items-center gap-2">
        <button @click="isAssignOpen = true" class="px-3 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90">Назначить</button>
      </div>
    </div>
    <!-- ./header -->

    <div class="flex h-[calc(100%-60px)]">
      <!-- Плавающая кнопка "Содержание" для мобильных -->
      <button 
        @click="toggleMobileStructure"
        class="mobile-content-button"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
        </svg>
      </button>

      <!-- Overlay для мобильной версии -->
      <div 
        v-if="isMobileStructureOpen" 
        @click="closeMobileStructure"
        class="mobile-overlay"
      ></div>

      <!-- structure -->
      <div 
        :class="[
          'course-sidebar',
          { 'course-sidebar--mobile-open': isMobileStructureOpen }
        ]"
      >
        <!-- Кнопка закрытия для мобильной версии -->
        <div class="title-structure flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <h3 class="text-lg font-semibold text-gray-900">Содержание</h3>
          <button
            @click="closeMobileStructure"
            class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Описание курса -->
        <div
          class="px-4 py-4 border-b border-gray-200 hover:bg-gray-100 cursor-pointer"
          :class="{ 'bg-blue-50 border-l-4 border-blue-500': contentType === 'description' }"
          @click="handleStructureItemClick('description')"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <span class="font-medium">Описание курса</span>
          </div>
        </div>

        <!-- Уроки -->
        <div class="py-4">
          <div class="mb-3 font-medium text-gray-700 flex items-center gap-2 px-4">
            <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
            </svg>
            Уроки
          </div>

          <div v-if="course.lessons && course.lessons.length > 0">
            <div
              v-for="(lesson, lessonIndex) in course.lessons"
              :key="lessonIndex"
              class="py-4 px-3 cursor-pointer hover:bg-gray-100 transition-colors"
              :class="{ 'bg-blue-50 border-l-4 border-primary': contentType === 'lesson' && currentLessonIndex === lessonIndex }"
              @click="handleStructureItemClick('lesson', lessonIndex)"
            >
              <div class="flex items-center gap-3">
                <div class="w-6 h-6 min-w-[24px] rounded-full bg-primary text-white text-xs flex items-center justify-center font-medium">
                  {{ lessonIndex + 1 }}
                </div>
                <span class="text-sm font-medium text-gray-700">{{ lesson.title || 'Урок без названия' }}</span>
              </div>
            </div>
          </div>

          <div v-else class="text-sm text-gray-500 text-center py-4">
            Уроки не найдены
          </div>
        </div>

        <!-- Тестирование -->
        <div class="py-4 border-t border-gray-200">
          <div class="mb-3 px-4 font-medium text-gray-700 flex items-center gap-2">
            <svg class="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Тестирование
          </div>

          <div
            class="py-4 px-4 cursor-pointer hover:bg-gray-100 transition-colors"
            :class="{ 'bg-purple-50 border-l-4 border-purple-500': contentType === 'tests' }"
            @click="handleStructureItemClick('tests')"
          >
            <div class="flex items-center gap-3">
              <div class="w-6 h-6 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center font-medium">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
              </div>
              <span class="text-sm font-medium text-gray-700">Список тестов</span>
            </div>
          </div>
        </div>
      </div>
      <!-- ./structure -->

      <!-- content -->
      <div class="content-course flex-1 p-6 overflow-y-auto">
        <!-- Описание курса -->
        <div v-if="contentType === 'description'" class="prose max-w-none">
          <div class="text-2xl font-bold text-gray-900 mb-4">О курсе</div>
          <div v-if="normalizedDescription" v-html="normalizedDescription" class="course-content"></div>
          <div v-else class="text-gray-500 text-center py-8">
            <svg class="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <p>Описание курса не заполнено</p>
          </div>

          <div v-if="course.lessons.length" class="flex items-center justify-end w-full py-6">
            <button
                class="px-6 py-2 bg-purple-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                @click="handleStructureItemClick('lesson', 0)"
            >
              Перейти к первому уроку
            </button>
          </div>
        </div>

        <!-- Урок -->
        <div v-else-if="contentType === 'lesson' && currentLesson" class="prose max-w-none">
          <div class="text-2xl font-bold text-gray-900 mb-4">{{ currentLesson.title }}</div>
          <div v-if="normalizedLessonContent" v-html="normalizedLessonContent" class="course-content"></div>
          <div v-else class="text-gray-500 text-center py-8">
            <svg class="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <p>Содержимое урока не заполнено</p>
          </div>

          <!-- Кнопки навигации по урокам -->
          <div class="flex justify-between mt-8">
            <button
              @click="goToPreviousLesson"
              :disabled="currentLessonIndex === 0"
              class="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Назад
            </button>
            <button
              @click="handleNavigationClick"
              :disabled="currentLessonIndex === course.lessons.length - 1 && !hasTests"
              class="px-6 py-2 bg-primary text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
              :class="[ isLastLesson && hasTests ? '!bg-purple-600' : '' ]"
            >
              {{ isLastLesson && hasTests ? 'Пройти тестирование' : 'Вперед' }}
            </button>
          </div>
          
        </div>

        <!-- Тестирование -->
        <div v-else-if="contentType === 'tests'" class="max-w-none">
          <div class="text-2xl font-bold text-gray-900 mb-6">Тестирование</div>
          
          <div v-if="course.tests && course.tests.length > 0" class="space-y-4">
            <div 
              v-for="test in course.tests" 
              :key="test.id"
              class="bg-white border border-gray-200 rounded-2xl p-4 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer"
              @click="openTest(test)"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="text-lg font-semibold text-gray-900 mb-2">{{ test.title }}</div>
                  
                  <div class="flex items-center gap-6 text-sm text-gray-600 mb-4">
                    <div class="flex items-center gap-2">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <span>{{ (test.numberOfQuestionsToDisplay ?? test.questionsCount) || 0 }} вопросов</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <span>{{ test.timeLimit }} мин</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                      </svg>
                      <span>Мин. {{ test.requiredCorrectAnswers }} правильных</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                      </svg>
                      <span>Попыток: {{ test.remainingAttempts }} из {{ test.attemptsAllowed }}</span>
                    </div>
                  </div>

                  <!-- Статус прохождения -->
                  <div class="flex items-center gap-2">
                    <div v-if="test.isCompleted" class="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                      Пройден
                    </div>
                    <div v-else-if="test.remainingAttempts > 0" class="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      Есть попытки
                    </div>
                    <div v-else class="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      Нет попыток
                    </div>
                    <div v-if="deadlineExceeded && !test.isCompleted" class="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      Дедлайн прошёл
                    </div>
                  </div>
                </div>
                
                <div class="ml-4">
                  <button
                    class="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    @click="openTest(test)"
                    :disabled="(!test.canRetake && test.remainingAttempts === 0) || (deadlineExceeded && !test.isCompleted)"
                  >
                    {{ (deadlineExceeded && !test.isCompleted) ? 'Дедлайн прошёл' : ((!test.canRetake && test.remainingAttempts === 0) ? 'Нет попыток' : (test.isCompleted ? 'Повторить тест' : 'Пройти тест')) }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="text-gray-500 text-center py-8">
            <svg class="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            </svg>
            <p>Тесты не найдены</p>
          </div>
        </div>

        <!-- Пустое состояние -->
        <div v-else class="text-center py-16">
          <svg class="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
          </svg>
          <div class="text-lg font-medium text-gray-900 mb-2">Добро пожаловать на курс</div>
          <p class="text-gray-500">Выберите урок или описание курса для начала изучения</p>
        </div>
      </div>
      <!-- ./content -->
    </div>
  </div>
    <UModal v-model="isAssignOpen">
      <AssignCourseModal :courseId="props.courseId" :preselectedUserIds="(course && course.accessibleUsers) ? course.accessibleUsers : []" @close="isAssignOpen = false" @assigned="onAssigned" />
    </UModal>
  
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineProps, defineEmits } from 'vue'
import apiService from '@/services/api'
import UModal from '@/components/ui/UModal.vue'
import AssignCourseModal from '@/components/course/AssignCourseModal.vue'
import { usePortalStore } from '@/stores/portalStore'

const props = defineProps({
  courseId: {
    type: String,
    required: true
  },
  openTestsTab: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'openTest'])

const course = ref(null)
const isAssignOpen = ref(false)
const portalStore = usePortalStore()
const isAdmin = computed(() => Boolean(portalStore.portalData?.isAdmin))
const contentType = ref('description')
const currentLessonIndex = ref(-1)
const isMobileStructureOpen = ref(false)

const currentLesson = computed(() => {
  if (contentType.value === 'lesson' && currentLessonIndex.value >= 0 && course.value?.lessons) {
    return course.value.lessons[currentLessonIndex.value]
  }
  return null
})

// Функция нормализации видео - заменяет iframe на video теги
const normalizeVideoEmbeds = (html) => {
  if (!html) return ''
  if (typeof window === 'undefined' || typeof document === 'undefined') return html
  
  const container = document.createElement('div')
  container.innerHTML = html
  
  const iframes = container.querySelectorAll('iframe.ql-video')
  if (!iframes.length) return html
  
  const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.m4v']
  
  iframes.forEach((iframe) => {
    const src = iframe.getAttribute('src') || ''
    const lowerSrc = src.toLowerCase()
    const hasVideoExtension = VIDEO_EXTENSIONS.some((ext) => lowerSrc.includes(ext))
    
    if (!src || !hasVideoExtension) return
    
    const video = document.createElement('video')
    video.className = iframe.className || 'ql-video'
    video.setAttribute('controls', '')
    video.setAttribute('playsinline', '')
    video.setAttribute('preload', 'metadata')
    video.style.width = '100%'
    video.style.display = 'block'
    video.src = src
    
    if (iframe.parentNode) {
      iframe.parentNode.replaceChild(video, iframe)
    }
  })
  
  return container.innerHTML
}

// Нормализованный контент текущего урока
const normalizedLessonContent = computed(() => {
  return normalizeVideoEmbeds(currentLesson.value?.content || '')
})

// Нормализованный контент описания курса
const normalizedDescription = computed(() => {
  return normalizeVideoEmbeds(course.value?.extendedDescription || '')
})

// Определяем, является ли текущий урок последним
const isLastLesson = computed(() => {
  return course.value?.lessons && currentLessonIndex.value === course.value.lessons.length - 1
})

// Определяем, есть ли тесты в курсе
const hasTests = computed(() => {
  return course.value?.tests && course.value.tests.length > 0
})

const deadlineExceeded = computed(() => {
  const d = course.value?.dueAt ? new Date(course.value.dueAt) : null
  if (!d) return false
  const end = new Date(d)
  end.setHours(23, 59, 59, 999)
  return Date.now() > end.getTime()
})

// Методы для управления мобильной панелью
const toggleMobileStructure = () => {
  isMobileStructureOpen.value = !isMobileStructureOpen.value
}

const closeMobileStructure = () => {
  isMobileStructureOpen.value = false
}

// Обработчик клика по элементам содержания
const handleStructureItemClick = (type, index = null) => {
  if (type === 'description') {
    showDescription()
  } else if (type === 'lesson' && index !== null) {
    showLesson(index)
  } else if (type === 'tests') {
    showTests()
  }
  
  // Закрываем мобильную панель после выбора
  closeMobileStructure()

  document.querySelector('.content-course').scrollTo({
    top: 0,
    behavior: 'smooth'
  });
}

const showDescription = () => {
  contentType.value = 'description'
  currentLessonIndex.value = -1
}

const showLesson = (index) => {
  currentLessonIndex.value = index
  contentType.value = 'lesson'
}

const showTests = () => {
  contentType.value = 'tests'
}

const openTest = (test) => {
  if ((test.canRetake || (test.remainingAttempts > 0 && !test.isCompleted)) && !(deadlineExceeded.value && !test.isCompleted)) {
    emit('openTest', {
      courseId: props.courseId,
      testId: test.id,
      test: test
    })
  }
}

const closeModal = () => {
  // Эмитим событие для закрытия модального окна
  emit('close')
}

const onAssigned = () => {
  isAssignOpen.value = false
}

// Функции для управления скроллом документа
const hideBodyScroll = () => {
  document.body.style.overflow = 'hidden'
}

const showBodyScroll = () => {
  document.body.style.overflow = ''
}

// Функции навигации по урокам
const goToPreviousLesson = () => {
  if (currentLessonIndex.value > 0) {
    showLesson(currentLessonIndex.value - 1)
  }

  document.querySelector('.content-course').scrollTo({
    top: 0,
    behavior: 'smooth'
  });
}

const goToNextLesson = () => {
  if (currentLessonIndex.value < course.value.lessons.length - 1) {
    showLesson(currentLessonIndex.value + 1)
  }
}

// Обработка клика по кнопке "Вперед"
const handleNavigationClick = () => {
  if (isLastLesson.value && hasTests.value) {
    showTests()
  } else {
    goToNextLesson()
  }

  document.querySelector('.content-course').scrollTo({
    top: 0,
    behavior: 'smooth'
  });
}

onMounted(async () => {
  hideBodyScroll()
  
  try {
    const data = await apiService.getCourse(props.courseId)
    course.value = data
    
    // Инициализируем пустые массивы, если их нет
    if (!data.lessons) data.lessons = []
    if (!data.tests) data.tests = []
    
    // Если передан флаг для открытия вкладки тестирования
    if (props.openTestsTab) {
      showTests()
    }

    console.log(course.value)
    
  } catch (error) {
    console.error('Ошибка при загрузке курса:', error)
  }
})

onUnmounted(() => {
  showBodyScroll()
})
</script>

<style scoped>
/* Стили для боковой панели */
.course-sidebar {
  width: 300px;
  background-color: #f9fafb;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  flex-shrink: 0; /* Убедимся, что сайдбар не сжимается */
  z-index: 10000!important;

  .title-structure {
    display: none;
  }
  
  /* Десктопная версия - всегда видна */
  @media (min-width: 768px) {
    display: block;
    position: relative;
  }
  
  /* Мобильная версия - скрыта по умолчанию */
  @media (max-width: 767px) {
    display: none;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 50;
    width: 280px;
    max-width: 80vw;
    transition: transform 0.3s ease;
    transform: translateX(-100%);
    box-shadow: 2px 0 5px rgba(0,0,0,0.1); /* Добавим тень для визуального отделения */
  }
}

@media (max-width: 767px) {
  .course-sidebar--mobile-open {
    display: block;
    transform: translateX(0);
  }

  .title-structure {
    display: block;
  }
}

/* Плавающая кнопка для мобильных */
.mobile-content-button {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  background-color: #574AEA;
  color: white;
  border-radius: 50%;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    transform: scale(1.05);
  }
  
  /* Скрыта на десктопе */
  @media (min-width: 768px) {
    display: none;
  }
}

/* Overlay для мобильной версии */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(4px);
  z-index: 40;
  
  /* Скрыт на десктопе */
  @media (min-width: 768px) {
    display: none;
  }
}

/* Стили для контента курса */
:deep(.course-content) {
  line-height: 1.7;
  color: #374151;
}

/* Используем глобальные стили для согласованности */
:deep(.course-content) h1 {
  font-size: 2rem;
  font-weight: 700;
  margin: 2rem 0 1rem 0;
  color: #111827;
}

:deep(.course-content) h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.5rem 0 1rem 0;
  color: #111827;
}

:deep(.course-content) h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem 0;
  color: #111827;
}

:deep(.course-content) p {
  margin-bottom: 1rem;
}

:deep(.course-content) ul {
  margin: 1rem 0;
  padding-left: 2rem;
  list-style: initial;
}

:deep(.course-content) ol {
  list-style: auto;
  margin: 1rem 0;
  padding-left: 2rem;
}

:deep(.course-content) li {
  margin-bottom: 0.5rem;
}

:deep(.course-content) a {
  color: #574AEA;
  text-decoration: underline;
}

:deep(.course-content) a:hover {
  color: #473add;
}

/* Стили для цитат */
:deep(.course-content) blockquote,
:deep(.course-content) .ql-blockquote {
  border-left: 4px solid #574AEA;
  padding: 12px 12px 12px 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: #6b7280;
  background: rgba(87, 74, 234, 0.05);
  border-radius: 0 8px 8px 0;
  position: relative;
}

/* Стили для размеров шрифта */
:deep(.course-content) .ql-size-small {
  font-size: 0.75em;
}

:deep(.course-content) .ql-size-large {
  font-size: 1.5em;
}

:deep(.course-content) .ql-size-huge {
  font-size: 2.5em;
}

/* Стили для выравнивания */
:deep(.course-content) .ql-align-left {
  text-align: left;
}

:deep(.course-content) .ql-align-center {
  text-align: center;
}

:deep(.course-content) .ql-align-right {
  text-align: right;
}

:deep(.course-content) .ql-align-justify {
  text-align: justify;
}

/* Стили для кода */
:deep(.course-content) code {
  background: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.875rem;
}

:deep(.course-content) pre {
  background: #1f2937;
  color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 1rem 0;
}

/* Стили для изображений */
:deep(.course-content) img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 1rem 0;
}

/* Мобильная адаптивность */
@media (max-width: 768px) {
  .w-full.h-full {
    height: 100vh;
    width: 100vw;
  }
  
  /* Header адаптация */
  .flex.item-center.justify-between.p-\[12px\] {
    padding: 16px 12px;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .text-xl {
    font-size: 1.125rem;
    line-height: 1.3;
  }
  
  /* Основной контейнер для мобильной версии */
  .flex.h-\[calc\(100\%-60px\)\] {
    flex-direction: row;
    height: calc(100% - 60px);
    position: relative;
  }
  
  /* Content area для мобильных */
  .flex-1 {
    width: 100%;
    padding: 16px;
    overflow-y: auto;
  }
  
  /* Sidebar navigation */
  .px-4.py-4 {
    padding: 12px 16px;
  }
  
  .py-4.px-3 {
    padding: 12px 16px;
  }
  
  .text-sm.font-medium {
    font-size: 0.875rem;
  }
  
  .max-w-4xl {
    max-width: 100%;
  }
  
  /* Test cards в мобильной версии */
  .grid.grid-cols-1.gap-6 {
    gap: 16px;
  }
  
  .rounded-2xl.p-6 {
    padding: 16px;
    border-radius: 16px;
  }
  
  .p-8 {
    padding: 16px;
  }
  
  /* Кнопки тестов */
  .px-6.py-3 {
    padding: 12px 16px;
    font-size: 0.875rem;
    min-height: 44px;
  }
  
  /* Статистика тестов */
  .flex.items-center.gap-6 {
    gap: 12px;
    flex-wrap: wrap;
  }
  
  .text-sm.text-gray-600 {
    font-size: 0.8rem;
  }
  
  /* Пустое состояние */
  .py-16 {
    padding: 32px 0;
  }
  
  .w-16.h-16 {
    width: 48px;
    height: 48px;
  }
  
  .text-lg {
    font-size: 1rem;
  }
  
  /* Навигационные кнопки уроков на мобильных */
  .flex.justify-between.mt-8 {
    flex-direction: column;
    gap: 12px;
    margin-top: 24px;
  }
  
  .flex.justify-between.mt-8 button {
    width: 100%;
    min-height: 48px;
  }

  /* Адаптация контента для мобильных */
  :deep(.course-content) blockquote,
  :deep(.course-content) .ql-blockquote {
    padding-left: 0.5rem;
    margin: 0.5rem 0;
  }
  
  :deep(.course-content) blockquote::before,
  :deep(.course-content) .ql-blockquote::before {
    font-size: 2rem;
    top: -5px;
    left: 5px;
  }
}
</style>
